import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, ShieldCheck, Tag, Upload, Settings, Download, ArrowRight } from 'lucide-react';

import logo_knowlimits from './../assets/logo-knowlimits.svg';

/* ─── Section wrapper with fade-in ─── */
function Section({ children, className = "", id, bgClass = "bg-white" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { 
      if (e.isIntersecting) setVisible(true); 
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section 
      ref={ref} 
      id={id} 
      className={`py-16 px-6 transition-all duration-700 ease-out ${bgClass} ${className}`}
      style={{ 
        opacity: visible ? 1 : 0, 
        transform: visible ? "translateY(0)" : "translateY(20px)" 
      }}
    >
      <div className="max-w-5xl mx-auto">{children}</div>
    </section>
  );
}

/* ─── Label chip ─── */
function Label({ children }) {
  return (
    <span className="inline-block text-[11px] font-bold text-blue-700 tracking-[0.2em] uppercase mb-4 px-3 py-1 bg-blue-50 rounded-full">
      {children}
    </span>
  );
}

/* ─── Divider ─── */
function Divider() {
  return <div className="h-px bg-slate-200 max-w-5xl mx-auto" />;
}

/* ═══════════════════════════════════════════════════
   SOCIAL PROOF
   ═══════════════════════════════════════════════════ */
function SocialProof() {
  return (
    <Section bgClass="bg-slate-50" className="py-8 text-center border-t border-slate-200">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-4">
        Používají výzkumníci z
      </p>
      <div className="flex justify-center items-center gap-12 flex-wrap opacity-70 hover:opacity-100 transition-opacity">
        <img src={logo_knowlimits} alt="KNOWLIMITS Logo" className="h-8 object-contain" />
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════
   THREE STATS
   ═══════════════════════════════════════════════════ */
function Stats() {
  const items = [
    {
      icon: <Clock className="w-6 h-6 text-blue-700" />,
      number: "5 min",
      title: "Rychlost",
      desc: "2 500 odpovědí roztříděných do 5 minut. Místo celého dne ručního přepisování v Excelu."
    },
    {
      icon: <Tag className="w-6 h-6 text-blue-700" />,
      number: "až 5× levnější",
      title: "Cena",
      desc: "5 000 respondentů za 1 550–3 390 Kč. Stejná agenturní práce: 10 000–29 000 Kč."
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-blue-700" />,
      number: "100 % kontrola",
      title: "Kontrola",
      desc: "Kategorie schvalujete vy. Upravíte, sloučíte, přejmenujete, spustíte znovu. Nikdy black box."
    },
  ];
  return (
    <Section>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((item, i) => (
          <div key={i} className="p-7 rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="mb-4 bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center">
              {item.icon}
            </div>
            <div className="text-2xl font-extrabold text-blue-700 mb-1">{item.number}</div>
            <div className="text-sm font-semibold text-slate-900 mb-2">{item.title}</div>
            <div className="text-[13px] text-slate-500 leading-relaxed">{item.desc}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════
   BEFORE / AFTER SHOWCASE
   ═══════════════════════════════════════════════════ */
function BeforeAfter() {
  const inputRows = [
    { id: "1p5w2", text: "Balení" },
    { id: "20ea3", text: "Cistota" },
    { id: "239w1", text: "Extra čistota" },
    { id: "9zyf4", text: "Čisté prádlo" },
    { id: "wz9w2", text: "Voňavá cistota" },
    { id: "0ct54", text: "Inspirace pro druhe" },
    { id: "0t494", text: "Umění, inspirace, originalita" },
    { id: "0ulf4", text: "že je v něčem speciální" },
    { id: "0b6a4", text: "Neco vymecneho" },
  ];
  const outputRows = [
    { cat: "ODLIŠNOST", n: 102 },
    { cat: "VÝJIMEČNOST", n: 99 },
    { cat: "JEDINEČNOST", n: 97 },
    { cat: "NEVÍM / NEROZUMÍM", n: 66 },
    { cat: "KVALITA", n: 65 },
    { cat: "INOVACE", n: 55 },
    { cat: "EKOLOGIE", n: 55 },
    { cat: "ORIGINALITA", n: 54 },
    { cat: "UMĚNÍ / KREATIVITA", n: 49 },
  ];

  return (
    <Section bgClass="bg-slate-50" id="ukazka">
      <div className="text-center mb-10">
        <Label>Jak to funguje v praxi</Label>
        <h2 className="text-3xl font-bold text-slate-900 mb-3">
          Z volného textu do strukturovaných dat
        </h2>
        <p className="text-sm text-slate-500 max-w-2xl mx-auto">
          Otázka: <em className="text-slate-700 font-medium">"Co se Vám vybaví, když se řekne ekologické čisticí prostředky?"</em>
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-stretch">
        
        {/* INPUT */}
        <div className="flex-1 bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex justify-between items-baseline mb-4 border-b border-slate-100 pb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Původní odpovědi respondentů
            </span>
            <span className="text-xs font-semibold text-slate-500">1 879 odpovědí</span>
          </div>
          <div className="flex flex-col gap-1">
            {inputRows.map((row, i) => (
              <div key={i} className={`flex gap-3 px-3 py-2 rounded-md ${i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                <span className="font-mono text-[11px] text-slate-400 min-w-[40px] pt-0.5">{row.id}</span>
                <span className="text-[13px] text-slate-600">„{row.text}"</span>
              </div>
            ))}
          </div>
          <div className="text-center text-xs text-slate-400 pt-3 italic">
            ... dalších 1 870 odpovědí
          </div>
        </div>

        {/* MIDDLE — AI indicator */}
        <div className="flex items-center justify-center py-4 lg:py-0">
          <div className="bg-blue-100 p-3 rounded-full text-blue-700 shadow-sm">
             <ArrowRight className="w-6 h-6 rotate-90 lg:rotate-0" />
          </div>
        </div>

        {/* OUTPUT */}
        <div className="flex-1 bg-white rounded-xl p-6 border border-blue-200 shadow-sm ring-1 ring-blue-50">
          <div className="flex justify-between items-baseline mb-4 border-b border-slate-100 pb-2">
            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
              Přiřazené kategorie
            </span>
            <span className="text-xs font-semibold text-blue-700">37 kategorií</span>
          </div>
          <div className="flex flex-col gap-1">
            {outputRows.map((row, i) => {
              const totalAnswers = 1879;
              const pct = ((row.n / totalAnswers) * 100).toFixed(1).replace('.', ',');
              return (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-md ${i % 2 === 0 ? 'bg-blue-50/50' : 'bg-white'}`}>
                  <span className="flex-1 text-[13px] font-semibold text-slate-800">{row.cat}</span>
                  <span className="text-[13px] font-bold text-blue-600/60 min-w-[32px] text-right">{pct}%</span>
                  <span className="text-[13px] font-bold text-blue-700 min-w-[32px] text-right">{row.n}</span>
                </div>
              );
            })}
          </div>
          <div className="text-center text-xs text-slate-400 pt-3 italic">
            dalších 28 kategorií
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════
   HOW IT WORKS — 3 STEPS
   ═══════════════════════════════════════════════════ */
function HowItWorks() {
  const steps = [
    {
      icon: <Upload className="w-7 h-7 text-blue-700" />,
      step: "01",
      title: "Nahrajte data",
      desc: "Excel nebo CSV s tisíci textových odpovědí respondentů."
    },
    {
      icon: <Settings className="w-7 h-7 text-blue-700" />,
      step: "02",
      title: "AI navrhne kategorie",
      desc: "Navržené kategorie upravíte, sloučíte nebo přidáte vlastní."
    },
    {
      icon: <Download className="w-7 h-7 text-blue-700" />,
      step: "03",
      title: "Stáhněte výsledky",
      desc: "Hotový Excel: odpověď | kategorie + statistika. Žádná migrace, žádný vendor lock-in."
    },
  ];

  return (
    <Section id="jak-to-funguje" className="scroll-mt-24">
      <div className="text-center mb-12">
        <Label>Jak to funguje</Label>
        <h2 className="text-3xl font-bold text-slate-900 mb-3">
          Tři kroky k výsledkům
        </h2>
        <p className="text-sm text-slate-500">Váš Excel na vstupu, Excel s výsledky na výstupu.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {steps.map((s, i) => (
          <div key={i} className="p-8 rounded-xl border border-slate-200 bg-white relative hover:-translate-y-1 transition-transform duration-300 shadow-sm">
            <div className="absolute top-6 right-6 text-4xl font-black text-slate-100">
              {s.step}
            </div>
            <div className="mb-6 bg-blue-50 w-14 h-14 rounded-lg flex items-center justify-center relative z-10">
              {s.icon}
            </div>
            <div className="text-lg font-bold text-slate-900 mb-2 relative z-10">
              <span className="text-slate-400 font-medium text-xs mr-2 uppercase tracking-wider">Krok {s.step}</span><br/>
              {s.title}
            </div>
            <div className="text-[13px] text-slate-500 leading-relaxed relative z-10">{s.desc}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════
   NUMBERS STRIP
   ═══════════════════════════════════════════════════ */
function NumbersStrip() {
  const items = [
    { n: "2 500", label: "odpovědí do pěti minut" },
    { n: "95 %+", label: "úspěšnost přiřazení" },
    { n: "10×", label: "rychlejší než ručně" },
  ];
  return (
    <Section bgClass="bg-blue-50" className="py-12 border-y border-blue-100">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {items.map((item, i) => (
          <div key={i} className="text-center">
            <div className="text-4xl font-extrabold text-blue-700 mb-2">{item.n}</div>
            <div className="text-sm font-medium text-slate-600">{item.label}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}


/* ═══════════════════════════════════════════════════
   MAIN PAGE EXPORT
   ═══════════════════════════════════════════════════ */
const HomePage = ({ isAuthenticated, isTemporary }) => {
  const navigate = useNavigate();
  const { hash } = useLocation(); // <-- Get the hash from the URL - to navigate from footer to 'jak to funguje'
  useEffect(() => {
    if (hash === '#jak-to-funguje') {
      const element = document.getElementById('jak-to-funguje');
      if (element) {
        // A slight timeout ensures the page is fully rendered before scrolling
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hash]);


  return (
    <div className="w-full text-slate-900 bg-white">
      
      {/* Hero Section */}
      <Section className="pt-24 pb-16 text-center">
        <Label>AI-powered open-end coding</Label>
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight tracking-tight max-w-3xl mx-auto">
          Automatické kódování otevřených odpovědí z průzkumů.
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          Analyzujte tisíce odpovědí za minuty. AI navrhne kategorie a automaticky odpovědi kategorizuje, vy jen odsouhlasíte a výsledek si okamžitě stáhnete.
        </p>
        
        <div className="flex justify-center">
          {isAuthenticated && !isTemporary ? (
            <button
              onClick={() => navigate('/analysis/create')}
              className="px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all transform hover:-translate-y-0.5"
            >
              Vytvořit novou analýzu
            </button>
          ) : ( 
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 w-full md:w-auto text-lg font-semibold text-white bg-blue-600 rounded-lg shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all transform hover:-translate-y-0.5"
              >
                Registrujte se nyní
              </button>
              
              <p className="max-w-xl text-sm text-slate-500 text-center my-2">
                Nechcete se registrovat? Aplikaci můžete vyzkoušet i bez registrace, stačí zadat e-mailovou adresu, na kterou Vám zašleme výsledky.
              </p>
              
              <button
                onClick={() => navigate('/analysis/email')} 
                className="px-8 py-3 w-full md:w-auto text-base font-semibold text-blue-700 bg-white border border-blue-200 rounded-lg shadow-sm hover:bg-blue-50 transition-all"
              >
                Vyzkoušet bez registrace
              </button>
            </div>
          )}
        </div>
      </Section>

      <SocialProof />
      <Divider />
      <Stats />
      <BeforeAfter />
      <HowItWorks />
      <NumbersStrip />
      
      {/* Bottom CTA Section */}
      <Section bgClass="bg-blue-700" className="py-20">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Vyzkoušejte to sami.
          </h2>
          <p className="text-lg text-blue-100 max-w-xl mx-auto mb-10">
            Registrace zdarma · první analýzu nahrajete během minut · platby kartou nebo převodem.
          </p>
          
          {isAuthenticated && !isTemporary ? (
            <button
              onClick={() => navigate('/analysis/create')}
              className="px-8 py-4 text-lg font-bold text-blue-700 bg-white rounded-lg shadow-lg hover:bg-blue-50 transition-all transform hover:-translate-y-0.5"
            >
              Vytvořit novou analýzu
            </button>
          ) : ( 
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 w-full md:w-auto text-lg font-bold text-blue-700 bg-white rounded-lg shadow-lg hover:bg-blue-50 transition-all transform hover:-translate-y-0.5"
              >
                Registrujte se nyní
              </button>
              
              <p className="max-w-xl text-sm text-blue-200 text-center mt-4 mb-2">
                Nechcete se registrovat? Aplikaci můžete vyzkoušet i bez registrace, stačí zadat e-mailovou adresu, na kterou Vám zašleme výsledky.
              </p>
              
              <button
                onClick={() => navigate('/analysis/email')} 
                className="px-8 py-3 w-full md:w-auto text-base font-semibold text-white bg-blue-800 border border-blue-600 rounded-lg hover:bg-blue-900 transition-all"
              >
                Vyzkoušet bez registrace
              </button>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

export default HomePage;